import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import moment, { Moment } from 'moment';
import React from 'react';
import { JobCostCodeSummaryGpDto } from 'src/api/FiretrolApiClient';
import { useCtcJob } from 'src/api/queries/ctc/useCtcJob';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { formatDate } from 'src/utils';

const NO_SUMMARY: JobCostCodeSummaryGpDto[] = [];

interface DateSummaryCardProps {
  jobId: number;
  jobNumber: string;
  isServiceJob?: boolean;
}

interface WbsDateSettings {
  bookedDate: Date | null;
  designStartDate: Date | null;
  submittalDueDate: Date | null;
  materialOnJobDate: Date | null;
  installStartDate: Date | null;
  installCompleteDate: Date | null;
  projectFinalDate: Date | null;
}

const initialValues: WbsDateSettings = {
  bookedDate: null,
  designStartDate: null,
  submittalDueDate: null,
  materialOnJobDate: null,
  installStartDate: null,
  installCompleteDate: null,
  projectFinalDate: null,
};

function calculateBusinessDays(start: Moment, end: Moment) {
  // Convert inputs to moment objects
  const startDate = moment(start.format('MM/DD/YYYY'));
  const endDate = moment(end.format('MM/DD/YYYY'));
  // Ensure the end date is after the start date
  if (endDate.isBefore(startDate)) {
    return 0;
  }

  let businessDays = 0;

  // Loop from start date to end date, incrementing day by day
  while (startDate.isSameOrBefore(endDate)) {
    // Check if the day is a weekday (not Saturday or Sunday)
    if (startDate.day() !== 0 && startDate.day() !== 6) {
      businessDays++;
    }
    // Move to the next day
    startDate.add(1, 'days');
  }

  return businessDays;
}

export const DateSummaryCard: React.FC<DateSummaryCardProps> = ({
  jobId,
  jobNumber,
  isServiceJob = false,
}) => {
  const wbsQuery = useJobWbs(jobId);
  const ctcQuery = useCtcJob(jobNumber, { enabled: !isServiceJob });
  const loading = wbsQuery.isLoading || ctcQuery.isLoading;

  const values: WbsDateSettings = React.useMemo(() => {
    const result = wbsQuery.data;
    if (!result) return initialValues;
    return {
      bookedDate: result.bookedDate ?? null,
      projectFinalDate: result.projectFinalDate ?? null,
      designStartDate: result.designStartDate ?? null,
      submittalDueDate: result.submittalDueDate ?? null,
      materialOnJobDate: result.materialOnJobDate ?? null,
      installStartDate: result.installStartDate ?? null,
      installCompleteDate: result.installCompleteDate ?? null,
    };
  }, [wbsQuery.data]);

  const summary = isServiceJob
    ? NO_SUMMARY
    : ctcQuery.data?.summary ?? NO_SUMMARY;

  const totalLaborHours = React.useMemo(() => {
    const target = summary.filter((w) => w?.costCode?.trim() === '10-40-00');
    let total = 0;
    target.forEach((t) => {
      total = total + t.ctcHours || 0;
    });
    return total;
  }, [summary]);

  const totalEngHours = React.useMemo(() => {
    const target = summary.filter(
      (w) => w?.costCode?.trim().slice(0, 5) === '10-50',
    );
    let total = 0;
    target.forEach((t) => {
      total = total + t.ctcHours || 0;
    });
    return total;
  }, [summary]);

  const renderDesignTable = (
    start: Date | null,
    end: Date | null,
    totalHours: number,
  ) => {
    if (!start || !end) {
      return (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Total Hours</Table.Th>
              <Table.Th>Bus Days</Table.Th>
              <Table.Th>Hr Per Wk</Table.Th>
              <Table.Th>FTEs</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>--</Table.Td>
              <Table.Td>--</Table.Td>
              <Table.Td>--</Table.Td>
              <Table.Td>--</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      );
    }

    const startDate = moment(start);
    const endDate = moment(end);
    const businessDays = calculateBusinessDays(startDate, endDate);
    const totalWeeks = moment(endDate.format('MM/DD/YYYY')).diff(
      moment(startDate.format('MM/DD/YYYY')),
      'weeks',
      false,
    );
    const hoursPerWeek =
      totalWeeks === 0 ? totalHours / 1 : totalHours / totalWeeks;
    const tWeekValue = totalWeeks === 0 ? 1 : totalWeeks;
    const ftes = hoursPerWeek / 40;

    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Total Hours</Table.Th>
            <Table.Th>Bus Days</Table.Th>
            <Table.Th>Weeks</Table.Th>
            <Table.Th>Hr Per Wk</Table.Th>
            <Table.Th>FTEs</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>{totalHours.toLocaleString()} Hours</Table.Td>
            <Table.Td>{businessDays.toLocaleString()} Days</Table.Td>
            <Table.Td>{tWeekValue.toLocaleString()}</Table.Td>
            <Table.Td>{hoursPerWeek.toLocaleString()}</Table.Td>
            <Table.Td>{ftes.toFixed(2)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Paper pos="relative">
      <LoadingOverlay visible={loading} />
      <Group justify="space-between" mb="md">
        <Title order={5}>Manhours</Title>
        <LinkButton size="compact-xs" to={`/jobs/${jobId}/wbs`}>
          Open
        </LinkButton>
      </Group>
      <Stack gap="md" style={{ height: 600, overflow: 'auto' }}>
        <TextInput
          label="Booked Date"
          value={values.bookedDate ? formatDate(values.bookedDate) : 'Not Set'}
          disabled
        />

        <Group>
          <TextInput
            label="Design Start Date"
            value={
              values.designStartDate
                ? formatDate(values.designStartDate)
                : 'Not Set'
            }
            disabled
          />
          <TextInput
            label="Submittal Due Date"
            value={
              values.submittalDueDate
                ? formatDate(values.submittalDueDate)
                : 'Not Set'
            }
            disabled
          />
        </Group>

        {renderDesignTable(
          values.designStartDate,
          values.submittalDueDate,
          totalEngHours,
        )}

        <TextInput
          label="Material on Job"
          value={
            values.materialOnJobDate
              ? formatDate(values.materialOnJobDate)
              : 'Not Set'
          }
          disabled
        />

        <Group>
          <TextInput
            label="Install Start Date"
            value={
              values.installStartDate
                ? formatDate(values.installStartDate)
                : 'Not Set'
            }
            disabled
          />
          <TextInput
            label="Install Comp Date"
            value={
              values.installCompleteDate
                ? formatDate(values.installCompleteDate)
                : 'Not Set'
            }
            disabled
          />
        </Group>

        {renderDesignTable(
          values.installStartDate,
          values.installCompleteDate,
          totalLaborHours,
        )}

        <TextInput
          label="Project Final Date"
          value={
            values.projectFinalDate
              ? formatDate(values.projectFinalDate)
              : 'Not Set'
          }
          disabled
        />
      </Stack>
    </Paper>
  );
};
